import React, { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Save,
  Star,
  Trash2,
  Users,
  Lock,
  Settings,
  Plus,
} from 'lucide-react';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { toast } from 'sonner';

export interface FilterPreset {
  id: string;
  name: string;
  description?: string;
  filters: TimesheetReportFilters;
  isDefault: boolean;
  isShared: boolean;
  createdBy: string;
  createdAt: Date;
  usageCount: number;
  type: 'personal' | 'team' | 'system';
}

interface FilterPresetsProps {
  currentFilters: TimesheetReportFilters;
  presets: FilterPreset[];
  onLoadPreset: (preset: FilterPreset) => void;
  onSavePreset: (preset: Omit<FilterPreset, 'id' | 'createdAt' | 'usageCount'>) => void;
  onDeletePreset: (presetId: string) => void;
  onSetDefault: (presetId: string) => void;
  currentUserId: string;
}

export const FilterPresets: React.FC<FilterPresetsProps> = ({
  currentFilters,
  presets,
  onLoadPreset,
  onSavePreset,
  onDeletePreset,
  onSetDefault,
  currentUserId,
}) => {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [shareWithTeam, setShareWithTeam] = useState(false);

  // Get default preset
  const defaultPreset = presets.find((p) => p.isDefault);

  // Separate presets by type
  const personalPresets = presets.filter((p) => p.type === 'personal');
  const teamPresets = presets.filter((p) => p.type === 'team');
  const systemPresets = presets.filter((p) => p.type === 'system');

  const handleSavePreset = () => {
    if (!presetName.trim()) {
      toast.error('Please enter a preset name');
      return;
    }

    const newPreset: Omit<FilterPreset, 'id' | 'createdAt' | 'usageCount'> = {
      name: presetName.trim(),
      description: presetDescription.trim() || undefined,
      filters: currentFilters,
      isDefault: setAsDefault,
      isShared: shareWithTeam,
      createdBy: currentUserId,
      type: shareWithTeam ? 'team' : 'personal',
    };

    onSavePreset(newPreset);
    
    // Reset form
    setPresetName('');
    setPresetDescription('');
    setSetAsDefault(false);
    setShareWithTeam(false);
    setShowSaveDialog(false);

    toast.success('Filter preset saved successfully');
  };

  const handleLoadPreset = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (preset) {
      onLoadPreset(preset);
      toast.success(`Loaded preset: ${preset.name}`);
    }
  };

  const handleDeletePreset = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (preset && window.confirm(`Delete preset "${preset.name}"?`)) {
      onDeletePreset(presetId);
      toast.success('Preset deleted');
    }
  };

  const handleSetDefault = (presetId: string) => {
    onSetDefault(presetId);
    toast.success('Default preset updated');
  };

  const getPresetIcon = (type: FilterPreset['type']) => {
    switch (type) {
      case 'personal':
        return <Lock className="h-3 w-3" />;
      case 'team':
        return <Users className="h-3 w-3" />;
      case 'system':
        return <Settings className="h-3 w-3" />;
    }
  };

  const getPresetBadgeVariant = (type: FilterPreset['type']) => {
    switch (type) {
      case 'personal':
        return 'secondary';
      case 'team':
        return 'default';
      case 'system':
        return 'outline';
    }
  };

  return (
    <div className="space-y-4">
      {/* Preset Selector */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Saved Presets</Label>
        <div className="flex gap-2">
          <Select onValueChange={handleLoadPreset}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Load a saved preset..." />
            </SelectTrigger>
            <SelectContent>
              {/* System Presets */}
              {systemPresets.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">
                    System Presets
                  </div>
                  {systemPresets.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>
                      <div className="flex items-center gap-2">
                        {getPresetIcon(preset.type)}
                        <span>{preset.name}</span>
                        {preset.isDefault && (
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </>
              )}

              {/* Team Presets */}
              {teamPresets.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 mt-2">
                    Team Presets
                  </div>
                  {teamPresets.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>
                      <div className="flex items-center gap-2">
                        {getPresetIcon(preset.type)}
                        <span>{preset.name}</span>
                        {preset.isDefault && (
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </>
              )}

              {/* Personal Presets */}
              {personalPresets.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 mt-2">
                    My Presets
                  </div>
                  {personalPresets.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>
                      <div className="flex items-center gap-2">
                        {getPresetIcon(preset.type)}
                        <span>{preset.name}</span>
                        {preset.isDefault && (
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </>
              )}

              {presets.length === 0 && (
                <div className="px-2 py-6 text-center text-sm text-gray-500">
                  No saved presets yet
                </div>
              )}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowSaveDialog(true)}
            title="Save current filters as preset"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Default Preset Info */}
        {defaultPreset && (
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
            Default: {defaultPreset.name}
          </div>
        )}
      </div>

      {/* Preset Management */}
      {presets.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Manage Presets</Label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {presets
              .filter((p) => p.type !== 'system') // Can't manage system presets
              .map((preset) => (
                <div
                  key={preset.id}
                  className="flex items-center justify-between p-2 rounded-lg border bg-white hover:bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">
                        {preset.name}
                      </span>
                      <Badge variant={getPresetBadgeVariant(preset.type)} className="h-5">
                        {getPresetIcon(preset.type)}
                      </Badge>
                      {preset.isDefault && (
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                      )}
                    </div>
                    {preset.description && (
                      <p className="text-xs text-gray-500 truncate">
                        {preset.description}
                      </p>
                    )}
                    <p className="text-xs text-gray-400">
                      Used {preset.usageCount} times
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    {!preset.isDefault && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleSetDefault(preset.id)}
                        title="Set as default"
                      >
                        <Star className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => handleDeletePreset(preset.id)}
                      title="Delete preset"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Save Preset Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Filter Preset</DialogTitle>
            <DialogDescription>
              Save your current filter configuration for quick access later.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="preset-name">Preset Name *</Label>
              <Input
                id="preset-name"
                placeholder="e.g., My Weekly Review"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="preset-description">Description (Optional)</Label>
              <Textarea
                id="preset-description"
                placeholder="Brief description of this preset..."
                value={presetDescription}
                onChange={(e) => setPresetDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={setAsDefault}
                  onChange={(e) => setSetAsDefault(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4" />
                  <span className="text-sm">Set as default preset</span>
                </div>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shareWithTeam}
                  onChange={(e) => setShareWithTeam(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span className="text-sm">Share with team</span>
                </div>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePreset} className="gap-2">
              <Save className="h-4 w-4" />
              Save Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
